import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Tag, Empty, Input, Button, Switch, Modal, message } from "antd";
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  SaveOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import moment from "moment";
import useCanManageLessonsLearned from "./shared/useCanManageLessonsLearned";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";
import { LESSONS_LEARNED_STATUS_LABELS } from "@iso/constants/incidentOptions";

const { TextArea } = Input;

const BRAND_BUTTON_PROPS = {
  style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" },
};

const contentBoxStyle = {
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
};

const STATUS_COLORS = {
  draft: "default",
  pending_review: "processing",
  approved: "blue",
  distributed: "success",
};

const LessonsLearnedPage = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [lessonsLearned, setLessonsLearned] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [togglingRequired, setTogglingRequired] = useState(false);

  const canManage = useCanManageLessonsLearned();

  useEffect(() => {
    if (incidentId) loadData();
  }, [incidentId]);

  const loadData = async (keepEditorContent = false) => {
    try {
      setLoading(true);
      const [incidentData, investigationData, lessonsData] = await Promise.all([
        incidentService.getById(incidentId),
        incidentService.getInvestigation(incidentId).catch(() => null),
        incidentService.getLessonsLearned(incidentId),
      ]);
      setIncident(incidentData);
      setInvestigation(investigationData);
      if (keepEditorContent) {
        setLessonsLearned(lessonsData);
      } else {
        applyLessons(lessonsData);
      }
    } catch (err) {
      console.error("Failed to load lessons learned data:", err);
      message.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  // The editor is seeded from the saved text when there is one, falling back
  // to a generated draft that has not been saved over yet.
  const applyLessons = (lessonsData) => {
    setLessonsLearned(lessonsData);
    setContent(lessonsData?.final_content || lessonsData?.ai_draft || "");
  };

  const isApproved = investigation?.status === "approved";
  const isDistributed = lessonsLearned?.status === "distributed";
  const canEdit = canManage && !isDistributed;
  const hasContent = !!content.trim();

  const handleGenerate = () => {
    Modal.confirm({
      title: "Generate Draft",
      content:
        "Build a Lessons Learned draft from the incident and investigation? Anything currently in the editor that has not been saved will be replaced.",
      okButtonProps: BRAND_BUTTON_PROPS,
      onOk: async () => {
        try {
          setGenerating(true);
          const updated = await incidentService.generateLessonsLearned(incidentId);
          setLessonsLearned(updated);
          // Show the draft that was just generated. applyLessons() prefers
          // saved content, which would leave the box unchanged here and make
          // the button look like it did nothing.
          setContent(updated?.ai_draft || updated?.final_content || "");
          message.success("Draft generated. Review and edit before distributing.");
          loadData(true);
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to generate draft."
          );
        } finally {
          setGenerating(false);
        }
      },
    });
  };

  const handleSave = async () => {
    if (!hasContent) {
      message.error("Please enter the lessons learned before saving.");
      return;
    }
    try {
      setSaving(true);
      const updated = await incidentService.updateLessonsLearned(
        incidentId,
        content.trim()
      );
      applyLessons(updated);
      message.success("Lessons learned saved. This no longer blocks closing the incident.");
      loadData();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save lessons learned."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDistribute = () => {
    Modal.confirm({
      title: "Distribute Lessons Learned",
      content:
        "Email these lessons learned to the relevant managers? The content is locked from further editing once distributed.",
      okButtonProps: BRAND_BUTTON_PROPS,
      onOk: async () => {
        try {
          setDistributing(true);
          const updated = await incidentService.distributeLessonsLearned(incidentId);
          applyLessons(updated);
          message.success("Lessons learned distributed.");
          loadData();
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to distribute lessons learned."
          );
        } finally {
          setDistributing(false);
        }
      },
    });
  };

  const handleToggleRequired = async (required) => {
    try {
      setTogglingRequired(true);
      const updated = await incidentService.toggleLessonsLearnedRequired(
        incidentId,
        required
      );
      setIncident(updated || incident);
      message.success(
        required
          ? "Lessons Learned is now required before this incident can be closed."
          : "Lessons Learned is no longer required to close this incident."
      );
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to update the requirement."
      );
    } finally {
      setTogglingRequired(false);
    }
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="loading-container"><Spin size="large" /></div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="not-found-container">
              <h2 className="not-found-title">Incident not found</h2>
              <span className="not-found-link" onClick={() => history.push("/incidents")}>
                Return to incidents
              </span>
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  const renderEditor = () => (
    <div className="form-card">
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {lessonsLearned && (
          <Tag color={STATUS_COLORS[lessonsLearned.status] || "default"} style={{ marginRight: 0 }}>
            {LESSONS_LEARNED_STATUS_LABELS[lessonsLearned.status] || lessonsLearned.status}
          </Tag>
        )}
        {lessonsLearned?.distributed_at && (
          <span style={{ fontSize: 12, color: "#667085" }}>
            Distributed on {moment(lessonsLearned.distributed_at).format("DD/MM/YYYY HH:mm")}
            {lessonsLearned.distributed_by_name
              ? ` by ${lessonsLearned.distributed_by_name}`
              : ""}
          </span>
        )}
      </div>

      {canManage && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            padding: "12px 16px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
          }}
        >
          <Switch
            checked={!!incident.lessons_learned_required}
            loading={togglingRequired}
            onChange={handleToggleRequired}
          />
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#101828" }}>
              Required before closing this incident
            </p>
            <p style={{ margin: 0, fontSize: 12, color: "#667085" }}>
              When on, the incident cannot be closed until these lessons learned are saved or distributed.
            </p>
          </div>
        </div>
      )}

      <p className="field-label">Lessons Learned</p>
      {canEdit ? (
        <TextArea
          rows={14}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What can we learn from this incident? What should others in the organisation know? Remove names and other identifying details before distributing."
        />
      ) : (
        <div style={{ ...contentBoxStyle, fontSize: 14, color: "#101828" }}>
          {content || "No lessons learned have been recorded for this incident."}
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleGenerate}
            loading={generating}
          >
            Generate Draft
          </Button>
          <Button
            className="btn-save-draft"
            onClick={handleSave}
            loading={saving}
            disabled={!hasContent}
          >
            <SaveOutlined /> Save
          </Button>
          <Button
            type="primary"
            className="btn-submit"
            onClick={handleDistribute}
            loading={distributing}
            disabled={!lessonsLearned || !hasContent}
          >
            <SendOutlined /> Distribute
          </Button>
        </div>
      )}

      {isDistributed && canManage && (
        <p style={{ marginTop: 16, fontSize: 12, color: "#667085" }}>
          Distributed lessons learned are locked and can no longer be edited.
        </p>
      )}

      {lessonsLearned?.ai_draft && lessonsLearned.ai_draft !== content && (
        <div style={{ marginTop: 24 }}>
          <p className="field-label" style={{ color: "#667085" }}>Generated Draft (Reference)</p>
          <div style={{ ...contentBoxStyle, fontSize: 13, color: "#667085" }}>
            {lessonsLearned.ai_draft}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          <button className="inv-back-button" onClick={() => history.push("/incidents")}>
            <ArrowLeftOutlined /> Back to Incidents
          </button>

          <div className="inv-header" style={{ marginBottom: 24 }}>
            <div>
              <h1 className="inv-title">Lessons Learned</h1>
              <p className="inv-subtitle">
                {incident.report_number} — {incident.description || ""}
              </p>
            </div>
          </div>

          {!isApproved ? (
            <div className="form-card" style={{ textAlign: "center", padding: 48 }}>
              <ClockCircleOutlined style={{ fontSize: 48, color: "#d0d5dd", marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, fontWeight: 600, color: "#101828", marginBottom: 8 }}>
                Investigation Not Complete
              </h3>
              <p style={{ fontSize: 14, color: "#667085" }}>
                Lessons learned will be available once the investigation has been approved by a Safety Admin.
              </p>
            </div>
          ) : !lessonsLearned && !canManage ? (
            <div className="form-card" style={{ textAlign: "center", padding: 48 }}>
              <Empty description="No lessons learned have been recorded for this incident." />
            </div>
          ) : (
            renderEditor()
          )}
        </InvestigationFormWrapper>
      </Box>
    </LayoutWrapper>
  );
};

export default LessonsLearnedPage;
